/**
 * Lumber Mill-specific component showing wood production bonus mechanics.
 * Styled for parchment theme.
 */
export function LumberMillInfo({ level, stars, assignedCount }: { level: number; stars: number; assignedCount: number }) {
    // Constants matching the backend LumberMillService
    const LEVEL_BONUSES: Record<number, number> = { 1: 1, 2: 2, 3: 4, 4: 6, 5: 9 };
    const LEVEL_INCREMENTS: Record<number, number> = { 1: 1, 2: 1, 3: 2, 4: 2, 5: 3 };
    const CHARACTER_BONUS_CHANCE = 20;

    // Calculate current bonuses
    const levelBonus = assignedCount > 0 ? (LEVEL_BONUSES[level] || 0) : 0;
    const maxCharacterBonusPerHour = assignedCount;
    const expectedCharacterBonusPerHour = assignedCount * (CHARACTER_BONUS_CHANCE / 100);

    const hasWorkers = assignedCount > 0;

    return (
        <div className="mb-6">
            <div className="flex items-center gap-3 mb-4">
                <span className="text-2xl">🪵</span>
                <h2 className="text-xl font-serif text-amber-900">Wood Production Bonus</h2>
            </div>

            <p className="text-amber-800 font-serif text-sm mb-4 leading-relaxed">
                The Lumber Mill increases your hex's wood production when characters are assigned to work here.
                Higher levels provide larger flat bonuses, and each assigned worker has a chance to produce extra wood each hour.
            </p>

            {/* Current Status */}
            <div className={`rounded-lg p-4 mb-4 border ${hasWorkers ? 'bg-green-100/40 border-green-600/40' : 'bg-amber-200/40 border-amber-500/40'}`}>
                <h3 className={`text-sm font-serif font-semibold uppercase tracking-wider mb-3 ${hasWorkers ? 'text-green-800' : 'text-amber-800'}`}>
                    {hasWorkers ? 'Active Bonuses' : 'No Workers Assigned'}
                </h3>
                {hasWorkers ? (
                    <div className="space-y-2 font-serif">
                        <div className="flex justify-between text-sm">
                            <span className="text-amber-700">Level {level} Bonus</span>
                            <span className="text-green-700 font-bold">+{levelBonus} wood/collection</span>
                        </div>
                        <div className="flex justify-between text-sm">
                            <span className="text-amber-700">Worker Chance Bonus ({assignedCount} workers)</span>
                            <span className="text-green-700 font-medium">~{expectedCharacterBonusPerHour.toFixed(1)} wood/hour avg</span>
                        </div>
                        <div className="text-xs text-amber-600 mt-2">
                            Each worker has a {CHARACTER_BONUS_CHANCE}% chance per hour to add +1 wood (max +{maxCharacterBonusPerHour}/hour)
                        </div>
                    </div>
                ) : (
                    <p className="text-amber-700 font-serif text-sm">
                        Assign at least one character to this Lumber Mill to activate the wood production bonuses.
                    </p>
                )}
            </div>

            {/* Level Bonus Table */}
            <div className="mb-4">
                <h3 className="text-sm font-serif font-semibold text-amber-900 mb-2">Level Bonus (requires workers)</h3>
                <p className="text-xs font-serif text-amber-700 mb-2">
                    When at least one character is assigned, you receive a flat wood bonus per collection based on the Lumber Mill's level.
                </p>
                <div className="overflow-x-auto">
                    <table className="w-full text-sm font-serif">
                        <thead>
                            <tr className="border-b border-amber-400/40">
                                <th className="text-left py-2 px-3 text-amber-800 font-medium">Level</th>
                                <th className="text-center py-2 px-3 text-amber-800 font-medium">Per-Level Bonus</th>
                                <th className="text-center py-2 px-3 text-amber-800 font-medium">Total Bonus</th>
                            </tr>
                        </thead>
                        <tbody>
                            {[1, 2, 3, 4, 5].map((lvl) => (
                                <tr
                                    key={lvl}
                                    className={`border-b border-amber-300/30 ${level === lvl ? 'bg-green-200/30' : ''}`}
                                >
                                    <td className="py-2 px-3 text-amber-900 font-medium">Level {lvl}</td>
                                    <td className="py-2 px-3 text-center text-amber-700">
                                        +{LEVEL_INCREMENTS[lvl]} wood
                                    </td>
                                    <td className="py-2 px-3 text-center text-green-700 font-medium">
                                        +{LEVEL_BONUSES[lvl]} wood
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Character Roll Bonus */}
            <div className="mb-4">
                <h3 className="text-sm font-serif font-semibold text-amber-900 mb-2">Worker Chance Bonus</h3>
                <p className="text-xs font-serif text-amber-700 mb-2">
                    Each character assigned to the Lumber Mill has a {CHARACTER_BONUS_CHANCE}% chance per hour to produce +1 additional wood.
                    This is rolled separately for each worker during each hourly collection period.
                </p>
                <div className="bg-amber-100/40 rounded-lg p-3 border border-amber-400/30">
                    <div className="grid grid-cols-2 gap-4 text-sm font-serif">
                        <div>
                            <span className="text-amber-700">Chance per worker/hour:</span>
                            <span className="ml-2 text-amber-900 font-medium">{CHARACTER_BONUS_CHANCE}%</span>
                        </div>
                        <div>
                            <span className="text-amber-700">Max slots (stars):</span>
                            <span className="ml-2 text-amber-900 font-medium">{stars}</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Example Calculation */}
            <div className="p-3 bg-amber-100/40 rounded-lg border border-amber-400/30">
                <h4 className="text-xs font-serif font-semibold text-amber-800 uppercase mb-2">Example Collection (3 hours)</h4>
                <p className="text-xs font-serif text-amber-700 mb-2">
                    With a Level {level} Lumber Mill and {Math.max(assignedCount, 3)} workers, collecting after 3 hours:
                </p>
                <div className="text-sm font-serif space-y-1">
                    <div className="flex justify-between">
                        <span className="text-amber-700">Level Bonus:</span>
                        <span className="text-green-700">+{LEVEL_BONUSES[level] || 0} wood (flat per collection)</span>
                    </div>
                    <div className="flex justify-between">
                        <span className="text-amber-700">Worker Rolls:</span>
                        <span className="text-amber-900">{Math.max(assignedCount, 3)} workers × 3 hours = {Math.max(assignedCount, 3) * 3} rolls</span>
                    </div>
                    <div className="flex justify-between">
                        <span className="text-amber-700">Expected from rolls:</span>
                        <span className="text-green-700">~{(Math.max(assignedCount, 3) * 3 * 0.2).toFixed(1)} wood (avg)</span>
                    </div>
                    <div className="flex justify-between">
                        <span className="text-amber-700">Maximum from rolls:</span>
                        <span className="text-amber-800">+{Math.max(assignedCount, 3) * 3} wood (if all succeed)</span>
                    </div>
                </div>
            </div>
        </div>
    );
}

/**
 * Herbalist Hut-specific component showing medicinal production bonus mechanics.
 * Styled for parchment theme.
 */
